// @file Vuex store Module for Section

/* eslint-disable no-param-reassign */
import { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { captureException, captureFetchException } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import sectionSorter from 'bits/section_sorter'
import { ACCESS_WITHDRAWN } from 'bits/snackbar_helper'
import { vSet } from 'bits/vue'
import { scrollToSectionId } from 'surface/dom_helpers'
import PadletApi from 'surface/padlet_api'
import type { Id, Section } from 'types'
import { nextTick } from 'vue'
import { isNew as isSectionNew } from 'vuexstore/helpers/section'
import { SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'

export default {
  namespaced: true,
  state: {
    sectionsFetched: false,
    sectionEntities: {},
    defaultSectionId: null,
    pendingSectionId: null,
    sectionBeingEditedId: null,
    actionMenuSectionId: null,
  },
  getters: {
    sections: (state, getters): Section[] => getters.sectionIds.map((id) => state.sectionEntities[id]),
    usableSections: (_state, getters): Section[] => getters.sections.filter((s) => !isSectionNew(s)),
    sortedSections: (_, getters): Section[] => sectionSorter.sortSections(getters.sections),
    sectionIds: (state): string[] => Object.keys(state.sectionEntities),
    numOfSections: (_, getters): number => getters.sectionIds.length,
    defaultSectionId: (state) => state.defaultSectionId,
    getArrayIndex:
      (_, getters) =>
      (sectionId: Id): number =>
        getters.sortedSections.findIndex((section: Section) => section.id === sectionId),
    sectionById:
      (state): Function =>
      (sectionId: Id | undefined): Section | null =>
        sectionId ? state.sectionEntities[sectionId] : null,
    areSectionsFetched: (state): boolean => state.sectionsFetched,
  },
  mutations: {
    FETCH_SECTIONS_SUCCESS(state, sections): void {
      const restorePendingSection = (newSectionEntities): void => {
        const pendingSection = state.sectionEntities[state.pendingSectionId]
        if (pendingSection) {
          newSectionEntities[state.pendingSectionId] = pendingSection
        }
      }

      const restoreEditedTitle = (newSectionEntities): void => {
        const sectionBeingEdited = state.sectionEntities[state.sectionBeingEditedId]
        const incomingSection = newSectionEntities[state.sectionBeingEditedId]
        if (sectionBeingEdited && incomingSection && sectionBeingEdited.title !== incomingSection.title) {
          incomingSection.title = sectionBeingEdited.title
        }
      }

      if (sections) {
        const newSectionEntities = {}
        sections.forEach((section) => {
          newSectionEntities[section.id] = section
        })
        restorePendingSection(newSectionEntities)
        restoreEditedTitle(newSectionEntities)
        state.sectionEntities = newSectionEntities // Trigger state change only once
      }
      state.sectionsFetched = true
    },
    ADD_PENDING_SECTION(state, pendingSection): void {
      const tempId = 'new_' + new Date().valueOf()
      state.sectionEntities = {
        ...state.sectionEntities,
        [tempId]: {
          ...pendingSection,
          id: tempId,
        },
      }
      state.pendingSectionId = tempId
    },
    ADD_SECTION(state, section): void {
      const newSectionEntities = { ...state.sectionEntities }
      // delete pending section
      delete newSectionEntities[state.pendingSectionId]
      // put new section in place of pending section
      state.sectionEntities = {
        ...newSectionEntities,
        [section.id]: section,
      }
      state.pendingSectionId = null
    },
    UPDATE_SECTION(state, { id, obj }): void {
      // vSet(state.sectionEntities[sectionId], 'title', title)
      vSet(state.sectionEntities, id, obj)
    },
    REMOVE_SECTION(state, id): void {
      if (state.pendingSectionId === id) {
        state.pendingSectionId = null
      }
      const newSectionEntities = { ...state.sectionEntities }
      delete newSectionEntities[id]
      state.sectionEntities = newSectionEntities
    },
    SET_DEFAULT_SECTION_ID(state, sectionId: Id): void {
      state.defaultSectionId = sectionId
    },
    SHOW_SECTION_ACTION_MENU(state, sectionId): void {
      state.actionMenuSectionId = sectionId
    },
    HIDE_SECTION_ACTION_MENU(state): void {
      state.actionMenuSectionId = null
    },
    START_EDITING_SECTION(state, sectionId): void {
      state.sectionBeingEditedId = sectionId
    },
    STOP_EDITING_SECTION(state): void {
      state.sectionBeingEditedId = null
    },
  },
  actions: {
    fetchSections({ dispatch, rootGetters }): void {
      PadletApi.WallSection.readAll({ wall: { id: rootGetters.wallId } })
        .then((fetchedData: JsonAPIResponse<Section>) => {
          dispatch('fetchSectionsSuccess', { fetchedData })
        })
        .catch((e) => {
          // Don't capture exception when it's 401, show a snackbar instead
          if (e.status === 401) {
            dispatch('snackbars/setSnackbar', ACCESS_WITHDRAWN, { root: true })
          } else {
            captureFetchException(e, { source: 'fetchSections' })
          }
        })
    },
    fetchSectionsSuccess({ commit, dispatch }, { fetchedData }: { fetchedData: JsonAPIResponse<Section> }): void {
      const sections = (fetchedData.data as Array<JsonAPIResource<Section>>).map((section) => section.attributes)
      commit('FETCH_SECTIONS_SUCCESS', sections)
      dispatch('nativeApp/postSurfaceState', null, { root: true })
    },

    refreshSections({ dispatch, commit }, payload: JsonAPIResponse<Section>): void {
      dispatch('fetchSectionsSuccess', { fetchedData: payload })
    },

    /* ------------
     * PANEL
     * ------------ */
    showSectionActionMenu({ commit }, { sectionId }): void {
      commit('SHOW_SECTION_ACTION_MENU', sectionId)
    },
    hideSectionActionMenu({ commit }): void {
      commit('HIDE_SECTION_ACTION_MENU')
    },

    /* ------------
     * INTERACTIONS
     * ------------ */
    openDefaultSectionTitleEdit({ dispatch, getters }): void {
      const defaultSectionId = getters.sectionIds[0] ?? null
      if (defaultSectionId == null) return
      dispatch('startEditingSection', { sectionId: Number(defaultSectionId) })
    },
    async deleteSection({ commit, state, dispatch, getters }, { sectionId }): Promise<void> {
      try {
        const existingSection = state.sectionEntities[sectionId]
        commit('STOP_EDITING_SECTION')
        if (existingSection != null) {
          await PadletApi.WallSection.delete(existingSection)
          commit('REMOVE_SECTION', sectionId)
          dispatch(
            'snackbar',
            { notificationType: SnackbarNotificationType.success, message: __('Section deleted') },
            { root: true },
          )
          // if deleted section is default, switch default to earliest section to match new default in backend
          if (sectionId === getters.defaultSectionId) {
            dispatch('changeDefaultSection', Number(getters.sortedSections[0]?.id))
          }
          dispatch('nativeApp/postSurfaceState', null, { root: true })
        }
      } catch (e) {
        dispatch(
          'snackbar',
          { notificationType: SnackbarNotificationType.error, message: __('Oops! Something went wrong.') },
          { root: true },
        )
        captureException(e)
      }
    },
    startEditingSection({ commit, dispatch }, { sectionId }): void {
      dispatch('stopEditingSection')
      commit('START_EDITING_SECTION', sectionId)
    },
    stopEditingSection({ commit, state }): void {
      if (state.sectionBeingEditedId && String(state.sectionBeingEditedId).startsWith('new_')) {
        commit('REMOVE_SECTION', state.sectionBeingEditedId)
      }
      commit('STOP_EDITING_SECTION')
    },
    startWithNoSections({ dispatch, getters }): void {
      if (getters.numOfSections === 0) {
        dispatch('startNewSection', { section: { sort_index: sectionSorter.getBottomSortIndex() } })
      }
    },
    startNewSection({ state, commit, dispatch }, { section }): void {
      dispatch('stopEditingSection')
      commit('ADD_PENDING_SECTION', section)
      commit('START_EDITING_SECTION', state.pendingSectionId)
    },
    async changeSectionTitle({ commit, state, rootGetters, dispatch }, { sectionId, title }): Promise<void> {
      if (state.sectionEntities[sectionId].title !== title) {
        commit('UPDATE_SECTION', {
          id: sectionId,
          obj: {
            ...state.sectionEntities[sectionId],
            title,
          },
        })
      }

      if (state.pendingSectionId) {
        // new section being edited
        await PadletApi.WallSection.create({
          ...state.sectionEntities[state.pendingSectionId],
          wall_id: rootGetters.wallId,
          title,
        }).then((sectionData: JsonAPIResponse<Section>) => {
          const data = (sectionData.data as JsonAPIResource<Section>).attributes
          commit('ADD_SECTION', data)
          dispatch('nativeApp/postSurfaceState', null, { root: true })
        })
      } else {
        PadletApi.WallSection.update({ ...state.sectionEntities[sectionId], title })
        // don't do it synchronously to the update because the user would already see it
        // as changed.
        dispatch('nativeApp/postSurfaceState', null, { root: true })
      }

      if (state.sectionBeingEditedId === sectionId) {
        commit('STOP_EDITING_SECTION')
      }
    },
    sortedSection({ commit, state, dispatch }, { sectionId, sortIndex, gapSize }): void {
      // isStuck is just a safeguard. In the expected flow, isStuck should not be the one to trigger the network call.
      // Any corrective action should have been done in a previous request.
      const sameIndex = state.sectionEntities[sectionId].sortIndex === sortIndex
      const isStuck = gapSize <= 4
      if (sameIndex && !isStuck) return

      const updatedObject = { ...state.sectionEntities[sectionId], sort_index: sortIndex }
      commit('UPDATE_SECTION', { id: sectionId, obj: updatedObject })
      PadletApi.WallSection.update({ ...updatedObject, gap_size: gapSize })
      dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    moveSectionLeft({ dispatch, getters }, { sectionId }): void {
      const index = getters.getArrayIndex(sectionId)

      // No-op if already leftmost
      if (index <= 0) return

      const section = getters.sortedSections[index]
      let sortIndex = section.sort_index
      let gapSize: number | null = null

      if (index === 1) {
        sortIndex = sectionSorter.getTopSortIndex()
      } else {
        const leftSection = getters.sortedSections[index - 2]
        const rightSection = getters.sortedSections[index - 1]
        sortIndex = (rightSection.sort_index + leftSection.sort_index) / 2
        gapSize = sortIndex - leftSection.sort_index
      }

      dispatch('sortedSection', { sectionId, sortIndex, gapSize })

      // nextTick ensures it's shifted before we scroll, s.t. the section is centralized on the screen.
      nextTick(() => scrollToSectionId(sectionId, { smooth: false, centralize: true }))
    },
    moveSectionRight({ dispatch, getters }, { sectionId }): void {
      const index = getters.getArrayIndex(sectionId)

      // No-op if already rightmost
      if (index < 0 || index >= getters.numOfSections - 1) return

      const section = getters.sortedSections[index]
      let sortIndex = section.sort_index
      let gapSize: number | null = null

      if (index === getters.numOfSections - 2) {
        sortIndex = sectionSorter.getBottomSortIndex()
      } else {
        const leftSection = getters.sortedSections[index + 1]
        const rightSection = getters.sortedSections[index + 2]
        sortIndex = (rightSection.sort_index + leftSection.sort_index) / 2
        gapSize = sortIndex - leftSection.sort_index
      }

      dispatch('sortedSection', { sectionId, sortIndex, gapSize })

      // nextTick ensures it's shifted before we scroll, s.t. the section is centralized on the screen.
      nextTick(() => scrollToSectionId(sectionId, { smooth: false, centralize: true }))
    },
    saveNewSectionToDraft({ commit, dispatch, rootGetters }, { title }): void {
      PadletApi.WallSection.create({
        sort_index: sectionSorter.getBottomSortIndex(),
        wall_id: rootGetters.wallId,
        title,
      }).then((sectionData: JsonAPIResponse<Section>) => {
        commit('ADD_SECTION', (sectionData.data as JsonAPIResource<Section>).attributes)
        dispatch('nativeApp/postSurfaceState', null, { root: true })
        dispatch(
          'draft/updateActiveDraft',
          { wall_section_id: Number((sectionData.data as JsonAPIResource<Section>).id) },
          { root: true },
        )
      })
    },
    changeDefaultSection({ commit, dispatch }, sectionId: Id): void {
      commit('SET_DEFAULT_SECTION_ID', sectionId)
      dispatch('post/updateMostRecentlyTouchedSection', sectionId, { root: true })
    },

    /* ------------
     * REMOTE
     * ------------ */
    newSectionRemote({ commit, dispatch }, section): void {
      commit('ADD_SECTION', section)
      dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    editSectionRemote({ commit, dispatch }, section): void {
      commit('UPDATE_SECTION', { id: section.id, obj: section })
      dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    deleteSectionRemote({ commit, dispatch }, section): void {
      commit('REMOVE_SECTION', section.id)
      dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
  },
}
